import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { ISimpleContentPageFields } from "../@types/contentful";
import { AuthUtils } from "../AuthUtils";

type UseAuthProps = {
    accessLevel?: ISimpleContentPageFields["accessLevel"];
};

// useAuth Hook to manage the authentication state of a page.
export function useAuth(props: UseAuthProps) {
    const { accessLevel } = props;

    // Router methods and properties
    const { replace, isReady, query } = useRouter();

    // Extract the accountNumber from the URL query params, considering it could be a string or an array of strings
    const accountNumberParam = query.accountNumber;
    // If accountNumberParam is an array, use the first value; otherwise, use it directly
    const accountNumber = Array.isArray(accountNumberParam) ? accountNumberParam[0] : accountNumberParam;

    // Determine if the current page is publicly accessible based on the access level
    const isPublicAccess = !accessLevel || accessLevel === "PublicAccess";

    // Refs to track the API call state, to prevent duplicate calls
    const isAuthenticatingRef = useRef(false);
    const isSettingAuth = useRef(false);

    // Determine if page should have authentication verified based on the access level
    const [shouldVerify, setShouldVerify] = useState(accessLevel === "AccountAccessOnly" || accessLevel === "AccountAccessPreferred");
    // Store the time of the last authentication verification attempt
    const [lastValidationTime, setLastValidationTime] = useState<number | null>(null);
    // Local state of whether the user is authenticated or not, initially set based on public access
    const [isAuthenticated, setIsAuthenticated] = useState(isPublicAccess);
    // State to indicate if there is an ongoing verification process
    const [isAuthenticating, setisAuthenticating] = useState(false);

    // Effect to handle authentication status check when the access level changes
    useEffect(() => {
        if (accessLevel !== "PublicAccess") {
            setShouldVerify(true);
            setLastValidationTime(Date.now());
        }
    }, [accessLevel, isAuthenticated]);

    // Effect to handle the automatic authentication via accountNumber in the query string
    useEffect(() => {
        const setAuth = async (secret: string | null) => {
            isSettingAuth.current = true;
            setisAuthenticating(true);

            // If the secret is null, remove accountNumber from the query string
            if (!secret) {
                const { accountNumber, ...rest } = query;
                await replace({ query: { ...rest } }, undefined, { shallow: true });
                isSettingAuth.current = false;
                return;
            }

            const isAuthSet = await AuthUtils.setAuthStatus(secret, accessLevel);

            if (isAuthSet) {
                setShouldVerify(true);
                setLastValidationTime(Date.now());
                const { accountNumber, ...rest } = query;
                await replace({ query: { ...rest } }, undefined, { shallow: true });
            }
            setisAuthenticating(false);
            isSettingAuth.current = false;
        };

        const shouldSetAuth = !!accountNumber && !isSettingAuth.current && isReady;
        if (shouldSetAuth) {
            const extractedAccountNumber = AuthUtils.extractAccountNumber(accountNumber);
            setAuth(extractedAccountNumber);
        }
    }, [accessLevel, accountNumber, isAuthenticated, isPublicAccess, isReady, query, replace]);

    // Effect to verify the authentication status
    useEffect(() => {
        if (isPublicAccess) {
            setIsAuthenticated(true);
            return;
        }

        const verifyAuth = async () => {
            isAuthenticatingRef.current = true;
            const isAuthenticatedResult = await AuthUtils.verifyAuthStatus(accessLevel);
            setIsAuthenticated(isAuthenticatedResult);
            setisAuthenticating(false);
            isAuthenticatingRef.current = false;
        };

        const shouldRunVerification = shouldVerify && !!lastValidationTime && !isAuthenticatingRef.current;
        if (shouldRunVerification) {
            setisAuthenticating(true);
            verifyAuth();
        }
    }, [accessLevel, isPublicAccess, lastValidationTime, shouldVerify]);

    return { isAuthenticated, shouldVerify, setShouldVerify, setLastValidationTime, isAuthenticating };
}
